// Pure helpers for the selected member's trail on the live map: where they
// came from in their current (or most recent) trip, drawn as a tail that fades
// towards its oldest end so the direction of travel shows.
import type { Schemas } from "@10twenty/api";
import { routePositions, wrapNear, type HistoryPoint, type Position } from "../history/history";

/** The trail covers at most this much recent history. */
export const TRAIL_WINDOW_MS = 60 * 60_000;
/** The trails of members on the move (not the selected one) cover this much. */
export const MOVING_TRAIL_WINDOW_MS = 15 * 60_000;
/** Ingest accepts fixes up to five minutes ahead, so the range reaches that far. */
const TRAIL_AHEAD_MS = 5 * 60_000;
/** A live fix this long after the history's last one is not joined to it (the server's gap rule). */
const JOIN_LIVE_MS = 10 * 60_000;
/** A live fix this far outside the last stop (plus its accuracy) means the member has left it. */
const LEFT_STOP_M = 50;
/** The trail is split into about this many evenly faded steps, so even a straight trip tapers. */
export const TRAIL_STEPS = 24;

const EARTH_RADIUS_M = 6_371_008.8;

/** One stretch of the trail; `fade` runs from 0 (oldest end) to 1 (newest). */
export interface TrailPiece {
  from: Position;
  to: Position;
  fade: number;
}

/** The history range to fetch for a trail of `windowMs` at `nowMs`, in whole seconds as the server expects. */
export function trailRange(nowMs: number, windowMs = TRAIL_WINDOW_MS): { from: string; to: string } {
  const instant = (ms: number) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
  return { from: instant(nowMs - windowMs), to: instant(nowMs + TRAIL_AHEAD_MS) };
}

/**
 * The points of the member's latest trip in `history` within the hour before
 * `nowMs` (and not before `notBeforeMs`): the one under way, or the one that
 * brought them to the stop they are at. Empty while paused, without such a
 * trip (one before a gap, or before more than that stop, is somewhere else),
 * and once the `live` fix has left that stop (the server has not seen the new
 * trip yet). A live fix newer than a trip still under way extends it, so the
 * tail reaches the marker, unless so much later that the server would see a gap.
 */
export function trailPoints(
  history: Schemas["History"],
  live: Schemas["LiveLocation"] | null,
  nowMs: number,
  notBeforeMs = -Infinity,
): HistoryPoint[] {
  return latestTripPoints(history, live, nowMs, notBeforeMs, TRAIL_WINDOW_MS, false);
}

/**
 * The trail of a member on the move: like {@link trailPoints}, but only the
 * trip still under way, within the last {@link MOVING_TRAIL_WINDOW_MS}.
 * Empty once they have stopped.
 */
export function movingTrailPoints(
  history: Schemas["History"],
  live: Schemas["LiveLocation"] | null,
  nowMs: number,
  notBeforeMs = -Infinity,
): HistoryPoint[] {
  return latestTripPoints(history, live, nowMs, notBeforeMs, MOVING_TRAIL_WINDOW_MS, true);
}

function latestTripPoints(
  history: Schemas["History"],
  live: Schemas["LiveLocation"] | null,
  nowMs: number,
  notBeforeMs: number,
  windowMs: number,
  underWayOnly: boolean,
): HistoryPoint[] {
  if (history.sharing === "paused") return [];
  const segments = history.segments;
  const lastSegment = segments[segments.length - 1];
  if (lastSegment?.kind === "stop" && live && Date.parse(live.ts) > Date.parse(lastSegment.end)) {
    const away = distanceM(live.lat, live.lon, lastSegment.lat, lastSegment.lon);
    if (away > Math.max(lastSegment.radius_m, LEFT_STOP_M) + live.accuracy_m) return [];
  }
  let index = segments.length - 1;
  while (index >= 0 && segments[index]!.kind !== "trip") index--;
  if (index < 0 || index < segments.length - 2) return [];
  const trip = segments[index] as Schemas["TripSegment"];
  if (trip.first_point < 0 || trip.first_point > trip.last_point || trip.last_point >= history.points.length) return [];
  if (history.points.slice(trip.last_point + 1).some((p) => p.gap_before)) return [];
  const underWay = index === segments.length - 1 && trip.last_point === history.points.length - 1;
  if (underWayOnly && !underWay) return [];
  const since = Math.max(nowMs - windowMs, notBeforeMs);
  const points = history.points.slice(trip.first_point, trip.last_point + 1).filter((p) => Date.parse(p.ts) >= since);
  const last = points[points.length - 1];
  if (underWay && live && last) {
    const after = Date.parse(live.ts) - Date.parse(last.ts);
    if (after > 0 && after <= JOIN_LIVE_MS) {
      points.push({ ts: live.ts, lat: live.lat, lon: live.lon, accuracy_m: live.accuracy_m, gap_before: false });
    }
  }
  return points;
}

/**
 * The trail as pieces between consecutive fixes, skipping gaps (`gap_before`),
 * cut into about `steps` even steps along its length, each faded by how far
 * along the trail its middle lies, so the oldest end is faint and the newest
 * solid. Positions stay on one world copy (see `routePositions`), near `near`
 * if given (the marker's longitude).
 */
export function trailPieces(points: readonly HistoryPoint[], near?: number, steps = TRAIL_STEPS): TrailPiece[] {
  let positions = routePositions(points);
  const first = positions[0];
  if (near !== undefined && first) {
    const shift = wrapNear(first[0], near) - first[0];
    if (shift !== 0) positions = positions.map(([lon, lat]) => [lon + shift, lat]);
  }
  const lengths: number[] = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const length = points[i]!.gap_before ? 0 : planeDistance(positions[i - 1]!, positions[i]!);
    lengths.push(length);
    total += length;
  }
  const pieces: TrailPiece[] = [];
  let along = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i]!.gap_before) continue;
    const [from, to] = [positions[i - 1]!, positions[i]!];
    const length = lengths[i - 1]!;
    if (total === 0) {
      pieces.push({ from, to, fade: 1 });
      continue;
    }
    const parts = Math.max(1, Math.round((length / total) * steps));
    for (let k = 0; k < parts; k++) {
      const a = lerp(from, to, k / parts);
      const b = lerp(from, to, (k + 1) / parts);
      pieces.push({ from: a, to: b, fade: (along + (length * (k + 0.5)) / parts) / total });
    }
    along += length;
  }
  return pieces;
}

function lerp([lon1, lat1]: Position, [lon2, lat2]: Position, t: number): Position {
  return [lon1 + (lon2 - lon1) * t, lat1 + (lat2 - lat1) * t];
}

/** Relative length in degrees, longitude scaled by latitude; only ratios matter here. */
function planeDistance([lon1, lat1]: Position, [lon2, lat2]: Position): number {
  const scale = Math.cos((((lat1 + lat2) / 2) * Math.PI) / 180);
  return Math.hypot((lon2 - lon1) * scale, lat2 - lat1);
}

/** Great-circle distance in metres. */
function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
